import type { Room } from '@hollowveil/shared';

export const roomCelestialObservatory: Room = {
      id: 'room_celestial_observatory',
      name: "L'Observatoire Céleste",
      description: "Un observatoire ancien où les étoiles des deux mondes forment ensemble la constellation du Voile.",
      size: [38, 18, 38],
      floorStyle: 'marble',
      ambiance: {
        realWorld: {
          backgroundColor: '#1a237e',
          ambientLightColor: '#5c6bc0',
          ambientLightIntensity: 0.4,
        },
        spectralWorld: {
          backgroundColor: '#0d0d1a',
          ambientLightColor: '#7c4dff',
          ambientLightIntensity: 0.5,
        },
      },
      objects: [
        // Central telescope platform
        {
          id: 'obj_telescope_platform',
          name: 'Plateforme du Télescope',
          type: 'static',
          modelId: 'block',
          position: [10, 0, 10],
          rotation: [0, 0, 0],
          scale: [4, 0.5, 4],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: { variant: 'stone' },
        },
        // Telescope
        {
          id: 'obj_telescope',
          name: 'Télescope Ancien',
          type: 'interactive',
          modelId: 'pillar',
          position: [10, 0.5, 10],
          rotation: [0.3, 0, 0],
          scale: [0.5, 2, 0.5],
          visibleInWorld: 'both',
          interactableInWorld: 'real',
          properties: { height: 3 },
          onInteract: [
            {
              type: 'showMessage',
              params: {
                message: '"Le télescope montre le ciel nocturne. Tu vois trois emplacements vides où devraient briller des orbes stellaires..."',
              },
            },
          ],
        },
        // Star Map (spectral world - shows constellation pattern)
        {
          id: 'obj_star_map',
          name: 'Carte Stellaire',
          type: 'interactive',
          modelId: 'block',
          position: [3, 1.5, 3],
          rotation: [0, 0, 0],
          scale: [3, 0.1, 3],
          visibleInWorld: 'spectral',
          interactableInWorld: 'spectral',
          properties: { variant: 'highlight' },
          onInteract: [
            {
              type: 'showMessage',
              params: {
                message: '"La Carte Stellaire révèle la Constellation du Voile :\n\n⭐ L\'Orbe du Nord va au NORD (position haute)\n⭐ L\'Orbe du Sud va au SUD (position basse)\n⭐ L\'Orbe Central va au CENTRE (entre les deux)\n\nLes piédestaux t\'attendent dans le monde réel."',
              },
            },
          ],
        },
        // Pedestal NORTH
        {
          id: 'obj_pedestal_north',
          name: 'Piédestal du Nord',
          type: 'interactive',
          modelId: 'pillar',
          position: [10, 0, 4],
          rotation: [0, 0, 0],
          scale: [1, 0.8, 1],
          visibleInWorld: 'both',
          interactableInWorld: 'real',
          properties: { height: 1.5 },
          onInteract: [
            {
              type: 'showMessage',
              params: {
                message: 'L\'Orbe du Nord est placé sur le piédestal ! Il s\'illumine d\'une lumière froide.',
              },
            },
          ],
        },
        // Pedestal SOUTH
        {
          id: 'obj_pedestal_south',
          name: 'Piédestal du Sud',
          type: 'interactive',
          modelId: 'pillar',
          position: [10, 0, 16],
          rotation: [0, 0, 0],
          scale: [1, 0.8, 1],
          visibleInWorld: 'both',
          interactableInWorld: 'real',
          properties: { height: 1.5 },
          onInteract: [
            {
              type: 'showMessage',
              params: {
                message: 'L\'Orbe du Sud est placé sur le piédestal ! Il s\'illumine d\'une lumière chaude.',
              },
            },
          ],
        },
        // Pedestal CENTER
        {
          id: 'obj_pedestal_center',
          name: 'Piédestal Central',
          type: 'interactive',
          modelId: 'pillar',
          position: [10, 0, 10],
          rotation: [0, 0, 0],
          scale: [1.2, 1, 1.2],
          visibleInWorld: 'both',
          interactableInWorld: 'real',
          properties: { height: 2 },
          onInteract: [
            {
              type: 'showMessage',
              params: {
                message: 'L\'Orbe Central complète la constellation ! Le Voile brille dans le ciel !',
              },
            },
            {
              type: 'openDoor',
              params: {
                doorId: 'obj_observatory_door',
              },
            },
          ],
        },
        // Stellar Orbs to collect
        {
          id: 'obj_orb_north',
          name: 'Orbe du Nord',
          type: 'interactive',
          modelId: 'orb',
          position: [3, 0.5, 17],
          rotation: [0, 0, 0],
          scale: [0.8, 0.8, 0.8],
          visibleInWorld: 'real',
          interactableInWorld: 'real',
          properties: {},
          onInteract: [
            {
              type: 'addToInventory',
              params: {
                itemId: 'orb_north',
                itemName: 'Orbe du Nord',
                itemDescription: 'Un orbe stellaire froid qui représente l\'étoile polaire.',
                itemIcon: 'orb',
              },
            },
            {
              type: 'showMessage',
              params: {
                message: 'Tu as récupéré l\'Orbe du Nord ! Place-le sur le bon piédestal.',
              },
            },
            {
              type: 'hideObject',
              params: {
                objectId: 'obj_orb_north',
              },
            },
          ],
        },
        {
          id: 'obj_orb_south',
          name: 'Orbe du Sud',
          type: 'interactive',
          modelId: 'orb',
          position: [17, 0.5, 3],
          rotation: [0, 0, 0],
          scale: [0.8, 0.8, 0.8],
          visibleInWorld: 'spectral',
          interactableInWorld: 'spectral',
          properties: {},
          onInteract: [
            {
              type: 'addToInventory',
              params: {
                itemId: 'orb_south',
                itemName: 'Orbe du Sud',
                itemDescription: 'Un orbe stellaire chaud qui représente le soleil du sud.',
                itemIcon: 'orb',
              },
            },
            {
              type: 'showMessage',
              params: {
                message: 'Tu as récupéré l\'Orbe du Sud ! Place-le sur le bon piédestal.',
              },
            },
            {
              type: 'hideObject',
              params: {
                objectId: 'obj_orb_south',
              },
            },
          ],
        },
        {
          id: 'obj_orb_center',
          name: 'Orbe Central',
          type: 'interactive',
          modelId: 'orb',
          position: [10, 0.5, 17],
          rotation: [0, 0, 0],
          scale: [1, 1, 1],
          visibleInWorld: 'spectral',
          interactableInWorld: 'spectral',
          properties: {},
          onInteract: [
            {
              type: 'addToInventory',
              params: {
                itemId: 'orb_center',
                itemName: 'Orbe Central',
                itemDescription: 'L\'orbe le plus brillant, cœur de la constellation du Voile.',
                itemIcon: 'orb',
              },
            },
            {
              type: 'showMessage',
              params: {
                message: 'Tu as récupéré l\'Orbe Central ! C\'est le cœur de la constellation.',
              },
            },
            {
              type: 'hideObject',
              params: {
                objectId: 'obj_orb_center',
              },
            },
          ],
        },
        // Ghost Astronomer
        {
          id: 'obj_astronomer_ghost',
          name: 'L\'Astronome Fantôme',
          type: 'interactive',
          modelId: 'spectral_remnant',
          position: [17, 0, 10],
          rotation: [0, -Math.PI / 2, 0],
          scale: [1.4, 1.4, 1.4],
          visibleInWorld: 'spectral',
          interactableInWorld: 'spectral',
          properties: {},
          onInteract: [
            {
              type: 'showRiddle',
              params: {
                riddle: {
                  id: 'riddle_astronomer',
                  npcId: 'obj_astronomer_ghost',
                  npcName: 'L\'Astronome Fantôme',
                  question: '"J\'ai passé ma vie à étudier les étoiles...\n\nAvant de te donner ma bénédiction, dis-moi :\nCombien d\'étoiles composent la constellation du Voile ?\n\n(Regarde la carte stellaire dans ce monde)"',
                  type: 'text_input',
                  expectedAnswers: ['3', 'trois', 'three'],
                  correctResponse: '"Trois étoiles... comme les trois mondes qui s\'entrelacent. Reçois ma bénédiction, voyageur des étoiles."',
                  incorrectResponse: '"Non... Compte les orbes sur la carte stellaire. La réponse y est inscrite."',
                  canRetry: true,
                  onCorrect: [
                    {
                      type: 'addToInventory',
                      params: {
                        itemId: 'astronomer_blessing',
                        itemName: 'Bénédiction de l\'Astronome',
                        itemDescription: 'La sagesse des étoiles coule en toi.',
                        itemIcon: 'star',
                      },
                    },
                  ],
                },
              },
            },
          ],
        },
        // Observatory door to final area
        {
          id: 'obj_observatory_door',
          name: 'Porte Céleste',
          type: 'static',
          modelId: 'animated_door',
          position: [19, 0, 10],
          rotation: [0, Math.PI / 2, 0],
          scale: [1.5, 2, 1],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        // Decorative pillars
        {
          id: 'obj_obs_pillar_1',
          name: 'Pilier Céleste',
          type: 'static',
          modelId: 'pillar',
          position: [2, 0, 10],
          rotation: [0, 0, 0],
          scale: [1, 1.5, 1],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: { height: 8 },
        },
        {
          id: 'obj_obs_pillar_2',
          name: 'Pilier Céleste',
          type: 'static',
          modelId: 'pillar',
          position: [18, 0, 2],
          rotation: [0, 0, 0],
          scale: [1, 1.5, 1],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: { height: 8 },
        },
        {
          id: 'obj_obs_pillar_3',
          name: 'Pilier Céleste',
          type: 'static',
          modelId: 'pillar',
          position: [18, 0, 18],
          rotation: [0, 0, 0],
          scale: [1, 1.5, 1],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: { height: 8 },
        },
        // Spectral stars floating
        {
          id: 'obj_star_1',
          name: 'Étoile Flottante',
          type: 'static',
          modelId: 'orb',
          position: [5, 4, 5],
          rotation: [0, 0, 0],
          scale: [0.3, 0.3, 0.3],
          visibleInWorld: 'spectral',
          interactableInWorld: 'spectral',
          properties: {},
        },
        {
          id: 'obj_star_2',
          name: 'Étoile Flottante',
          type: 'static',
          modelId: 'orb',
          position: [15, 5, 15],
          rotation: [0, 0, 0],
          scale: [0.25, 0.25, 0.25],
          visibleInWorld: 'spectral',
          interactableInWorld: 'spectral',
          properties: {},
        },
        {
          id: 'obj_star_3',
          name: 'Étoile Flottante',
          type: 'static',
          modelId: 'orb',
          position: [8, 6, 12],
          rotation: [0, 0, 0],
          scale: [0.35, 0.35, 0.35],
          visibleInWorld: 'spectral',
          interactableInWorld: 'spectral',
          properties: {},
        },
        // === Additional decorations for enlarged observatory (38x38) ===
        // More pillars
        {
          id: 'obj_obs_pillar_4',
          name: 'Pilier Céleste',
          type: 'static',
          modelId: 'pillar',
          position: [32, 0, 8],
          rotation: [0, 0, 0],
          scale: [1, 1.4, 1],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: { height: 7 },
        },
        {
          id: 'obj_obs_pillar_5',
          name: 'Pilier Céleste',
          type: 'static',
          modelId: 'pillar',
          position: [8, 0, 32],
          rotation: [0, 0, 0],
          scale: [1, 1.4, 1],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: { height: 7 },
        },
        {
          id: 'obj_obs_pillar_6',
          name: 'Pilier Céleste',
          type: 'static',
          modelId: 'pillar',
          position: [32, 0, 32],
          rotation: [0, 0, 0],
          scale: [1, 1.4, 1],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: { height: 7 },
        },
        // Columns
        {
          id: 'obj_obs_column_1',
          name: 'Colonne',
          type: 'static',
          modelId: 'column',
          position: [24, 0, 4],
          rotation: [0, 0, 0],
          scale: [1.1, 1.3, 1.1],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        {
          id: 'obj_obs_column_2',
          name: 'Colonne',
          type: 'static',
          modelId: 'column',
          position: [4, 0, 24],
          rotation: [0, 0, 0],
          scale: [1.1, 1.3, 1.1],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        // Statues
        {
          id: 'obj_obs_statue_1',
          name: 'Statue du Ciel',
          type: 'static',
          modelId: 'statue',
          position: [28, 0, 14],
          rotation: [0, -Math.PI / 4, 0],
          scale: [1.2, 1.4, 1.2],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        {
          id: 'obj_obs_statue_2',
          name: 'Statue du Ciel',
          type: 'static',
          modelId: 'statue',
          position: [14, 0, 28],
          rotation: [0, Math.PI / 4, 0],
          scale: [1.2, 1.4, 1.2],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        // Benches for stargazing
        {
          id: 'obj_obs_bench_1',
          name: 'Banc',
          type: 'static',
          modelId: 'bench',
          position: [20, 0, 24],
          rotation: [0, Math.PI / 2, 0],
          scale: [1, 1, 1],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        {
          id: 'obj_obs_bench_2',
          name: 'Banc',
          type: 'static',
          modelId: 'bench',
          position: [24, 0, 20],
          rotation: [0, 0, 0],
          scale: [1, 1, 1],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        // Lanterns
        {
          id: 'obj_obs_lantern_1',
          name: 'Lanterne Céleste',
          type: 'static',
          modelId: 'lantern',
          position: [6, 0, 14],
          rotation: [0, 0, 0],
          scale: [1.3, 1.3, 1.3],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        {
          id: 'obj_obs_lantern_2',
          name: 'Lanterne Céleste',
          type: 'static',
          modelId: 'lantern',
          position: [34, 0, 18],
          rotation: [0, 0, 0],
          scale: [1.3, 1.3, 1.3],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        {
          id: 'obj_obs_lantern_3',
          name: 'Lanterne Céleste',
          type: 'static',
          modelId: 'lantern',
          position: [18, 0, 34],
          rotation: [0, 0, 0],
          scale: [1.3, 1.3, 1.3],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        // Altar
        {
          id: 'obj_obs_altar',
          name: 'Autel Stellaire',
          type: 'static',
          modelId: 'altar',
          position: [26, 0, 26],
          rotation: [0, Math.PI / 4, 0],
          scale: [1.2, 1, 1.2],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        // Candles
        {
          id: 'obj_obs_candles_1',
          name: 'Bougies',
          type: 'static',
          modelId: 'candles',
          position: [26, 0.5, 26],
          rotation: [0, 0, 0],
          scale: [1, 1, 1],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        // Rocks
        {
          id: 'obj_obs_rock_1',
          name: 'Rocher',
          type: 'static',
          modelId: 'rock',
          position: [30, 0, 28],
          rotation: [0, 1.5, 0],
          scale: [1, 0.8, 1],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        {
          id: 'obj_obs_rock_2',
          name: 'Rocher',
          type: 'static',
          modelId: 'rock',
          position: [28, 0, 30],
          rotation: [0, 2.8, 0],
          scale: [0.8, 0.6, 0.8],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        // Debris
        {
          id: 'obj_obs_debris',
          name: 'Débris',
          type: 'static',
          modelId: 'debris',
          position: [34, 0, 34],
          rotation: [0, 0.5, 0],
          scale: [1.2, 1, 1.2],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        // More spectral stars
        {
          id: 'obj_star_4',
          name: 'Étoile Flottante',
          type: 'static',
          modelId: 'orb',
          position: [28, 5, 10],
          rotation: [0, 0, 0],
          scale: [0.28, 0.28, 0.28],
          visibleInWorld: 'spectral',
          interactableInWorld: 'spectral',
          properties: {},
        },
        {
          id: 'obj_star_5',
          name: 'Étoile Flottante',
          type: 'static',
          modelId: 'orb',
          position: [10, 4.5, 28],
          rotation: [0, 0, 0],
          scale: [0.32, 0.32, 0.32],
          visibleInWorld: 'spectral',
          interactableInWorld: 'spectral',
          properties: {},
        },
        {
          id: 'obj_star_6',
          name: 'Étoile Flottante',
          type: 'static',
          modelId: 'orb',
          position: [22, 6, 22],
          rotation: [0, 0, 0],
          scale: [0.4, 0.4, 0.4],
          visibleInWorld: 'spectral',
          interactableInWorld: 'spectral',
          properties: {},
        },
        {
          id: 'obj_star_7',
          name: 'Étoile Flottante',
          type: 'static',
          modelId: 'orb',
          position: [34, 5.5, 20],
          rotation: [0, 0, 0],
          scale: [0.22, 0.22, 0.22],
          visibleInWorld: 'spectral',
          interactableInWorld: 'spectral',
          properties: {},
        },
      ],
      puzzles: [
        {
          id: 'puzzle_constellation',
          name: 'La Constellation du Voile',
          type: 'placement',
          state: 'active',
          description: 'Place les trois orbes stellaires sur les bons piédestaux.',
          hint: 'La carte stellaire dans le monde spectral montre l\'emplacement correct de chaque orbe.',
          linkedObjectIds: ['obj_pedestal_north', 'obj_pedestal_south', 'obj_pedestal_center'],
          solution: {
            type: 'items',
            expected: ['orb_north', 'orb_south', 'orb_center'],
          },
          onSolve: [
            {
              type: 'showMessage',
              params: {
                message: 'La constellation du Voile brille dans le ciel ! La porte céleste s\'ouvre.',
              },
            },
          ],
        },
      ],
      connections: [
        {
          targetRoomId: 'room_forbidden_library',
          position: [0, 0, 10],
          direction: 'west',
          locked: false,
        },
        {
          targetRoomId: 'room_final_sanctuary',
          position: [20, 0, 10],
          direction: 'east',
          locked: false,
        },
      ],
      initialWorld: 'real',
      hasSpectralLayer: true,
      palette: 'night',
      biome: 'mountain',
      weather: 'snow',
      weatherIntensity: 0.4,
      npcs: [
        {
          id: 'npc_eagle_1',
          type: 'eagle',
          name: 'Aigle Royal',
          position: [20, 3, 10],
          scale: 3,
          idleAnimation: 'fly',
          petAnimation: 'chirp',
          petRadius: 3.5,
          petCooldown: 4000,
          isPettable: true,
          visibleInWorld: 'both',
        },
        {
          id: 'npc_goat_1',
          type: 'goat',
          name: 'Chèvre de Montagne',
          position: [8, 0, 25],
          scale: 3,
          idleAnimation: 'graze',
          petAnimation: 'happy',
          petRadius: 3,
          petCooldown: 2500,
          isPettable: true,
          visibleInWorld: 'both',
        },
        {
          id: 'npc_wolf_1',
          type: 'wolf',
          name: 'Loup Gardien',
          position: [30, 0, 30],
          scale: 3,
          idleAnimation: 'sit',
          petAnimation: 'nuzzle',
          petRadius: 3,
          petCooldown: 3500,
          isPettable: true,
          visibleInWorld: 'both',
        },
        {
          id: 'npc_marmot_1',
          type: 'marmot',
          name: 'Marmotte Joueuse',
          position: [15, 0, 8],
          scale: 2.5,
          idleAnimation: 'stand',
          petAnimation: 'jump',
          petRadius: 2.5,
          petCooldown: 2000,
          isPettable: true,
          visibleInWorld: 'both',
        },
        {
          id: 'npc_owl_1',
          type: 'owl',
          name: 'Hibou Sage',
          position: [35, 2.5, 15],
          scale: 2.5,
          idleAnimation: 'sit',
          petAnimation: 'chirp',
          petRadius: 2.5,
          petCooldown: 3000,
          isPettable: true,
          visibleInWorld: 'both',
        },
      ],
};
